import React, { useState, useEffect, Suspense } from 'react'
import { Outlet, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../App'
import { signOut } from '../lib/supabase'
import { isPageAllowed, pathToPageKey } from '../lib/pagePermissions'
import { LayoutDashboard, Car, CalendarDays, CalendarRange, Users, Building2, DollarSign, BarChart2, Settings, Wrench, LogOut, Menu, X, UserCheck, ClipboardCheck, Lock, Inbox, Send, MapPin } from 'lucide-react'

const NAV = [
  { to: '/', key: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', exact: true, group: 'Overview' },
  { to: '/fleet', key: 'fleet', icon: Car, label: 'Fleet', group: 'Overview' },
  { to: '/hq-fleet', key: 'hq-fleet', icon: Car, label: 'Peacock Fleet', franchiseOnly: true, group: 'Overview' },
  { to: '/onboarding', key: 'onboarding', icon: ClipboardCheck, label: 'Onboarding', group: 'Operations' },
  { to: '/rentals', key: 'rentals', icon: CalendarDays, label: 'Rentals', group: 'Operations' },
  { to: '/calendar', key: 'calendar', icon: CalendarRange, label: 'Calendar', group: 'Operations' },
  { to: '/turo', key: 'turo', icon: Inbox, label: 'Turo Inbox', group: 'Operations' },
  { to: '/live-map', key: 'live-map', icon: MapPin, label: 'Live Map', adminOnly: true, group: 'Operations' },
  { to: '/broker-availability', key: 'broker-availability', icon: Send, label: 'Broker Availability', group: 'Partners & Money' },
  { to: '/owners', key: 'owners', icon: Users, label: 'Owners', group: 'Partners & Money' },
  { to: '/partners', key: 'partners', icon: Building2, label: 'Partners', group: 'Partners & Money' },
  { to: '/payouts', key: 'payouts', icon: DollarSign, label: 'Payouts', group: 'Partners & Money' },
  { to: '/reports', key: 'reports', icon: BarChart2, label: 'Reports', group: 'Partners & Money' },
  { to: '/renters', key: 'renters', icon: UserCheck, label: 'Renters', group: 'Manage' },
  { to: '/maintenance', key: 'maintenance', icon: Wrench, label: 'Maintenance', group: 'Manage' },
  { to: '/users', key: 'users', icon: Users, label: 'Team', franchiseOnly: true, group: 'Manage' },
  { to: '/settings', icon: Settings, label: 'Settings', adminOnly: true, group: 'Manage' },
]

export default function AdminShell() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  // Close sidebar on route change (mobile nav)
  useEffect(() => { setSidebarOpen(false) }, [location.pathname])

  // Close on Escape
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') setSidebarOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  async function handleSignOut() {
    await signOut()
    navigate('/login')
  }

  // Filter nav items based on role AND per-user page permissions.
  //  - adminOnly     → HQ admins only (super_admin / admin), hidden from franchises
  //  - franchiseOnly → shown only to franchise admins (e.g. the read-only Peacock fleet)
  //  - otherwise     → shown to both HQ admins and franchise admins (RLS scopes the data)
  //  - item.key      → additionally hidden if the user's per-user page permissions
  //                    exclude it. Permissions can only remove access, never add it,
  //                    and never restrict a super_admin.
  const isAdmin = profile?.role === 'super_admin' || profile?.role === 'admin'
  const isFranchise = profile?.role === 'franchise_admin'
  const navItems = NAV.filter(item => {
    if (item.franchiseOnly && !isFranchise) return false
    if (item.adminOnly && !isAdmin) return false
    if (item.key && !isPageAllowed(profile, item.key)) return false
    return true
  })
  const consoleLabel = isFranchise
    ? (profile?.location?.name || 'Franchise') + ' Console'
    : 'Admin Console'

  // Route guard: if the user lands on (or links to) a section their permissions
  // don't allow, bounce them to their first available section rather than
  // rendering the blocked page. If they have no available sections, show a notice.
  const currentKey = pathToPageKey(location.pathname)
  const routeBlocked = currentKey && !isPageAllowed(profile, currentKey)

  let mainContent
  if (routeBlocked && navItems.length > 0) {
    mainContent = <Navigate to={navItems[0].to} replace />
  } else if (routeBlocked) {
    mainContent = (
      <div className="content">
        <div className="card" style={{ maxWidth: 460, margin: '48px auto', textAlign: 'center', padding: 32 }}>
          <Lock size={28} style={{ color: 'var(--text3)', marginBottom: 12 }} />
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>No sections enabled</div>
          <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.6 }}>
            Your account doesn't have access to any sections yet. Please contact your administrator.
          </div>
        </div>
      </div>
    )
  } else {
    // Pages are lazy-loaded; keep the sidebar/topbar on screen while the next page's code downloads.
    mainContent = <Suspense fallback={<div className="content"><div className="loading">Loading…</div></div>}><Outlet /></Suspense>
  }

  return (
    <div className="app-shell">
      {/* Tap-outside overlay (mobile) */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <div className="logo-tile">
            <img src="/logo.png" alt="Peacock Rentals" style={{ width: 26, height: 26, objectFit: 'contain' }} />
          </div>
          <div className="logo-text">
            <h1>Peacock Rentals</h1>
            <span>{consoleLabel}</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          {navItems.map(({ to, icon: Icon, label, exact, sub, group }, i) => {
            const prevGroup = i > 0 ? navItems[i - 1].group : null
            const showHeader = group && group !== prevGroup
            return (
              <React.Fragment key={to}>
                {showHeader && <div className="nav-section">{group}</div>}
                <NavLink
                  to={to} end={exact}
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                  style={sub ? { paddingLeft: 36, fontSize: 13, opacity: 0.85 } : {}}
                >
                  <Icon size={sub ? 13 : 16} />{label}
                </NavLink>
              </React.Fragment>
            )
          })}
        </nav>

        <div className="sidebar-footer">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div className="avatar">{profile?.name?.[0] || 'A'}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{profile?.name || 'Admin'}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{isFranchise ? (profile?.location?.name || 'Franchise') : 'Administrator'}</div>
            </div>
          </div>
          <button className="nav-item" onClick={handleSignOut} style={{ padding: '8px 0' }}>
            <LogOut size={16} />Sign Out
          </button>
        </div>
      </aside>

      {/* Main area */}
      <main className="main">
        {/* Mobile-only top strip with hamburger */}
        <div className="mobile-header">
          <button className="hamburger" onClick={() => setSidebarOpen(o => !o)} aria-label="Menu">
            {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <img src="/logo.png" alt="" style={{ width: 22, height: 22, objectFit: 'contain' }} />
            <span style={{ fontFamily: 'DM Serif Display, serif', fontSize: 16, color: 'var(--gold)' }}>Peacock Rentals</span>
          </div>
          <div style={{ width: 34 }} /> {/* spacer to center logo */}
        </div>
        {mainContent}
      </main>
    </div>
  )
}
